import React, { useState, useEffect } from "react";
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from "react-intl";
import { getMarketplaceEntities } from '../../../ducks/marketplaceData.duck';
import { NamedLink, ListingCard } from "../../../components";
import { getCarouselItemsPerSlide } from '../../../util/uiHelpers';

import css from './RecentListings.module.css';

const ListingSection = ({ titleId, listings, loading, error }) => {
  const [itemsPerSlide, setItemsPerSlide] = useState(getCarouselItemsPerSlide);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const updateItemsPerSlide = () => setItemsPerSlide(getCarouselItemsPerSlide());

    typeof window !== 'undefined' && window.addEventListener('resize', updateItemsPerSlide);
    return () => window.removeEventListener('resize', updateItemsPerSlide);
  }, []);

  useEffect(() => {
    setCurrentIndex(0);
  }, [listings.length, itemsPerSlide]);

  const maxIndex = Math.max(Math.ceil(listings.length / itemsPerSlide) - 1, 0);
  const handlePrev = () => setCurrentIndex(prevIndex => Math.max(prevIndex - 1, 0));
  const handleNext = () => setCurrentIndex(prevIndex => Math.min(prevIndex + 1, maxIndex));

  if (!loading && !error && listings.length === 0) {
    return null;
  }

  return (
    <div className={css.listingsWrapper}>
      <h3 className={css.header}>
        <FormattedMessage id={titleId} />
      </h3>

      {loading && <p><FormattedMessage id="RecentListing.loading" /></p>}
      {error && <p className={css.error}>Error: {error.message}</p>}

      {listings.length > 0 && (
        <div className={css.carouselContainer}>
          <button className={css.scrollLeft} onClick={handlePrev}>
            {'<'}
          </button>
          <div
            className={css.carouselWrapper}
            style={{ gridTemplateColumns: `repeat(${itemsPerSlide}, minmax(0, 1fr))` }}
          >
            {listings
              .slice(currentIndex * itemsPerSlide, (currentIndex + 1) * itemsPerSlide)
              .map(l => (
                <ListingCard
                  className={css.listingCard}
                  key={l.id.uuid}
                  listing={l}
                  showAuthorInfo={false}
                />
              ))}
          </div>
          <button className={css.scrollRight} onClick={handleNext}>
            {'>'}
          </button>
        </div>
      )}
    </div>
  );
};

const RecentListingsComponent = props => {
  const {
    recentListings,
    recentLoading,
    recentError,
    mostViewedListings,
    mostViewedLoading,
    mostViewedError,
    // vintageListings,
    // vintageLoading,
    // vintageError,
    hockeyError,
    soccerError,
    footballError,
    baseballError,
    basketballError,
    baseballListings,
    baseballLoading,
    basketballListings,
    basketballLoading,
    footballListings,
    footballLoading,
    soccerListings,
    soccerLoading,
    hockeyListings,
    hockeyLoading,
    mostWatchedListings,
    mostWatchedLoading,
    mostWatchedError,
  } = props;

  const redirectToSearch = category => {
    if (typeof window !== 'undefined') {
      window.location.href = `/s?pub_level2=${category}`;
    }
  };

  return (
    <div style={{ backgroundColor: '#fff' }}>
      <ListingSection
        titleId="RecentListing.header1"
        listings={recentListings}
        loading={recentLoading}
        error={recentError}
      />

      <ListingSection
        titleId="RecentListing.mostViewed"
        listings={mostViewedListings}
        loading={mostViewedLoading}
        error={mostViewedError}
      />
      <ListingSection
        titleId="RecentListing.mostWatched"
        listings={mostWatchedListings}
        loading={mostWatchedLoading}
        error={mostWatchedError}
      />

      {/* <ListingSection
        titleId="RecentListing.vintage"
        listings={vintageListings}
        loading={vintageLoading}
        error={vintageError}
      /> */}

      <ListingSection
        titleId="RecentListing.baseball"
        listings={baseballListings}
        loading={baseballLoading}
        error={baseballError}
      />

      <ListingSection
        titleId="RecentListing.basketball"
        listings={basketballListings}
        loading={basketballLoading}
        error={basketballError}
      />

      <ListingSection
        titleId="RecentListing.football"
        listings={footballListings}
        loading={footballLoading}
        error={footballError}
      />

      <ListingSection
        titleId="RecentListing.soccer"
        listings={soccerListings}
        loading={soccerLoading}
        error={soccerError}
      />

      <ListingSection
        titleId="RecentListing.hockey"
        listings={hockeyListings}
        loading={hockeyLoading}
        error={hockeyError}
      />

      <div className={css.cardsBtnsCont}>
        <button onClick={() => redirectToSearch('baseballcards')} className={css.sportsCardsBtns}>
          <FormattedMessage id="RecentListing.baseball" />
        </button>
        <button onClick={() => redirectToSearch('footballcards')} className={css.sportsCardsBtns}>
          <FormattedMessage id="RecentListing.football" />
        </button>
        <button onClick={() => redirectToSearch('basketballcards')} className={css.sportsCardsBtns}>
          <FormattedMessage id="RecentListing.basketball" />
        </button>
        <button onClick={() => redirectToSearch('pokemoncards')} className={css.sportsCardsBtns}>
          <FormattedMessage id="RecentListing.pokemon" />
        </button>
        <button onClick={() => redirectToSearch('wrestlingcards')} className={css.sportsCardsBtns}>
          <FormattedMessage id="RecentListing.wrestling" />
        </button>
        <button onClick={() => redirectToSearch('misccards')} className={css.sportsCardsBtns}>
          <FormattedMessage id="RecentListing.misc" />
        </button>
      </div>
      <div style={{ display: 'flex' }}>
        <NamedLink name="SearchPage" className={css.browseMoreLink}>
          <FormattedMessage id="RecentListing.browseMore1" />
        </NamedLink>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const {
    listings: recentRefs,
    loading: recentLoading,
    error: recentError,
    mostViewedListings: mostViewedRefs,
    mostViewedLoading,
    mostViewedError,
    // vintageListings: vintageRefs,
    // vintageLoading,
    // vintageError,
    hockeyError,
    soccerError,
    footballError,
    basketballError,
    baseballError,
    baseballListings: baseballRefs,
    baseballLoading,
    basketballListings: basketballRefs,
    basketballLoading,
    footballListings: footballRefs,
    footballLoading,
    soccerListings: soccerRefs,
    soccerLoading,
    hockeyListings: hockeyRefs,
    hockeyLoading,
    mostWatchedListings: mostWatchedRefs,
    mostWatchedLoading,
    mostWatchedError,
  } = state.LandingPage;

  return {
    recentListings: getMarketplaceEntities(state, recentRefs),
    recentLoading,
    recentError,
    mostViewedListings: getMarketplaceEntities(state, mostViewedRefs),
    mostViewedLoading,
    mostViewedError,
    // vintageListings: getMarketplaceEntities(state, vintageRefs),
    // vintageLoading,
    // vintageError,
    hockeyError,
    soccerError,
    footballError,
    basketballError,
    baseballError,
    baseballListings: getMarketplaceEntities(state, baseballRefs),
    baseballLoading,
    basketballListings: getMarketplaceEntities(state, basketballRefs),
    basketballLoading,
    footballListings: getMarketplaceEntities(state, footballRefs),
    footballLoading,
    soccerListings: getMarketplaceEntities(state, soccerRefs),
    soccerLoading,
    hockeyListings: getMarketplaceEntities(state, hockeyRefs),
    hockeyLoading,
    mostWatchedListings: getMarketplaceEntities(state, mostWatchedRefs),
    mostWatchedLoading,
    mostWatchedError,
  };
};

const RecentListings = compose(
  connect(mapStateToProps),
  injectIntl
)(RecentListingsComponent);

export default RecentListings;